import { yupResolver } from '@hookform/resolvers/yup';
import Image from 'next/image';
import { Controller, useForm } from 'react-hook-form';
import { object, string } from 'yup';
import { LoginFormStyle } from '../../styles/components/login';
import { Button, Input } from '../common/ui';
import PasswordInputWithToggle from '../common/ui/PasswordInputWithToggle';

export interface ILoginFormValues {
  email?: string;
  password?: string;
}

export interface ILoginFormProps extends ILoginFormValues {
  onFormSubmit: (formData: ILoginFormValues) => Promise<void>;
  onOAuthLogin: (
    provider: 'github' | 'linkedin' | 'microsoft' | 'google'
  ) => Promise<void>;
  onForgotPasswordClick: () => void;
  onRegisterClick: () => void;
  labels?: {
    emailLabel: string;
    passwordLabel: string;
    forgotButtonLabel: string;
    submitButtonLabel: string;
    registerLabel: string;
    registerButtonLabel: string;
    alternativeLoginMethodsTitle: string;
  };
  validation: {
    emailNotValidMessage: string;
    emailRequiredMessage: string;
    passwordRequiredMessage: string;
  };
}

const LoginForm = (props: ILoginFormProps) => {
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<ILoginFormValues>({
    resolver: yupResolver(
      object().shape({
        email: string()
          .email(props.validation.emailNotValidMessage)
          .required(props.validation.emailRequiredMessage),
        password: string().required(props.validation.passwordRequiredMessage),
      })
    ),
  });

  const showAlternativeLoginMethods = false;

  const onSubmit = (data: any) => {
    props.onFormSubmit(data);
    reset();
  };

  const onSubmitOAuth = (
    provider: 'github' | 'linkedin' | 'microsoft' | 'google'
  ) => {
    props.onOAuthLogin(provider);
    reset();
  };

  return (
    <div className={LoginFormStyle.container}>
      <div className={LoginFormStyle.subContainer}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Controller
            name="email"
            control={control}
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <Input
                label={props.labels?.emailLabel}
                type={'email'}
                name={'email'}
                id={'email'}
                value={value}
                error={errors.email?.message}
                onChange={(e: any) => {
                  onChange(e.target.value);
                }}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <PasswordInputWithToggle
                name={'password'}
                id={'password'}
                value={value}
                error={errors.password?.message}
                onChange={(e: any) => {
                  onChange(e.target.value);
                }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}>
                  <div>{props.labels?.passwordLabel}</div>
                  <Button
                    onClick={props.onForgotPasswordClick}
                    color={'minimal'}>
                    {props.labels?.forgotButtonLabel}
                  </Button>
                </div>
              </PasswordInputWithToggle>
            )}
          />
          <hr className="separator-thin" />
          <Button fluid type="submit">
            {props.labels?.submitButtonLabel}
          </Button>
          <div className={LoginFormStyle['bottomSection']}>
            {props.labels?.registerLabel}
            <Button
              onClick={props.onRegisterClick}
              style={{ marginLeft: '5px' }}
              type="button"
              color="minimal">
              {props.labels?.registerButtonLabel}
            </Button>
          </div>
        </form>

        {showAlternativeLoginMethods && (
          <>
            <div className="separator-with-text">
              <div className="separator-with-text-line"></div>
              <span>{props.labels?.alternativeLoginMethodsTitle}</span>
              <div className="separator-with-text-line"></div>
            </div>

            <Button
              hasIcon={true}
              onClick={() => onSubmitOAuth('google')}
              fluid
              color="outline">
              <Image
                quality={90}
                src={'/assets/images/icons/GoogleIcon.svg'}
                alt={'google'}
                width={20}
                height={20}
              />
              Google
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default LoginForm;
